/**
 * Relations Hooks
 * React Query hooks for relations API
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    getRelations,
    getRelationsForCollection,
    getRelation,
    createRelation,
    updateRelation,
    deleteRelation,
    type Relation,
    type CreateRelationPayload,
    type UpdateRelationPayload,
} from '@/api/relations';
import { fieldsKeys } from './useFieldsQuery';
import { toast } from 'sonner';

// Query keys
export const relationsKeys = {
    all: ['relations'] as const,
    list: () => [...relationsKeys.all, 'list'] as const,
    collection: (collection: string) => [...relationsKeys.all, 'collection', collection] as const,
    detail: (collection: string, field: string) => [...relationsKeys.all, 'detail', collection, field] as const,
};

// ============== Queries ==============

export function useRelations() {
    return useQuery({
        queryKey: relationsKeys.list(),
        queryFn: () => getRelations(),
    });
}

export function useRelationsForCollection(collection: string) {
    return useQuery({
        queryKey: relationsKeys.collection(collection),
        queryFn: () => getRelationsForCollection(collection),
        enabled: !!collection,
    });
}

export function useRelation(collection: string, field: string) {
    return useQuery({
        queryKey: relationsKeys.detail(collection, field),
        queryFn: async () => {
            try {
                return await getRelation(collection, field);
            } catch {
                // Return null if relation doesn't exist (e.g., non-relational field)
                return null;
            }
        },
        enabled: !!collection && !!field,
        retry: false, // Don't retry on 404
    });
}

/**
 * Hook to get M2M relation info for an alias field
 * For M2M, the relation is stored on the junction collection with one_collection/one_field
 * pointing back to this collection/field
 */
export function useRelationM2M(collection: string, field: string, enabled = true) {
    const { data: allRelations, isLoading } = useRelations();

    // Find the M2M relation where one_collection matches and one_field matches
    const m2mRelation = allRelations?.find(r => 
        r.meta?.one_collection === collection && 
        r.meta?.one_field === field &&
        r.meta?.junction_field // Must have junction_field to be M2M
    ) || null;

    // If we found the M2M relation, also find the related side relation
    const relatedRelation = m2mRelation && allRelations?.find(r =>
        r.collection === m2mRelation.collection && // Same junction collection
        r.field === m2mRelation.meta?.junction_field // The junction_field
    ) || null;

    return {
        data: m2mRelation,
        relatedRelation,
        junctionCollection: m2mRelation?.collection || null,
        junctionFieldCurrent: m2mRelation?.field || null,
        junctionFieldRelated: m2mRelation?.meta?.junction_field || null,
        relatedCollection: relatedRelation?.related_collection || null,
        sortField: m2mRelation?.meta?.sort_field || null,
        isLoading: enabled && isLoading,
    };
}

// ============== Mutations ==============

export function useCreateRelation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (payload: CreateRelationPayload) => createRelation(payload),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: relationsKeys.list() });
            queryClient.invalidateQueries({ queryKey: relationsKeys.collection(data.collection) });
            // Also invalidate fields since relation changes affect field display
            queryClient.invalidateQueries({ queryKey: fieldsKeys.all });
            toast.success('Relation created');
        },
        onError: (error: Error) => {
            toast.error(`Failed to create relation: ${error.message}`);
        },
    });
}

export function useUpdateRelation(collection: string, field: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (payload: UpdateRelationPayload) => updateRelation(collection, field, payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: relationsKeys.list() });
            queryClient.invalidateQueries({ queryKey: relationsKeys.collection(collection) });
            queryClient.invalidateQueries({ queryKey: relationsKeys.detail(collection, field) });
            // Also invalidate fields since relation changes affect field display
            queryClient.invalidateQueries({ queryKey: fieldsKeys.all });
            toast.success('Relation updated');
        },
        onError: (error: Error) => {
            toast.error(`Failed to update relation: ${error.message}`);
        },
    });
}

export function useDeleteRelation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ collection, field }: { collection: string; field: string }) =>
            deleteRelation(collection, field),
        onSuccess: (_, { collection, field }) => {
            queryClient.invalidateQueries({ queryKey: relationsKeys.list() });
            queryClient.invalidateQueries({ queryKey: relationsKeys.collection(collection) });
            queryClient.removeQueries({ queryKey: relationsKeys.detail(collection, field) });
            // Also invalidate fields since relation changes affect field display
            queryClient.invalidateQueries({ queryKey: fieldsKeys.all });
            toast.success('Relation deleted');
        },
        onError: (error: Error) => {
            toast.error(`Failed to delete relation: ${error.message}`);
        },
    });
}

// ============== Utilities ==============

/**
 * Get relation type from relation data
 */
export function getRelationType(relation: Relation): 'm2o' | 'o2m' | 'm2m' | 'unknown' {
    // M2O: has related_collection but no junction fields
    // O2M: inverse of M2O
    // M2M: has junction fields

    if (relation.meta?.one_collection && relation.meta?.junction_field) {
        return 'm2m';
    }

    if (relation.related_collection && !relation.meta?.one_collection) {
        return 'm2o';
    }

    if (relation.meta?.one_collection && relation.meta?.one_field) {
        return 'o2m';
    }

    return 'unknown';
}

/**
 * Get human-readable relation type label
 */
export function getRelationTypeLabel(type: 'm2o' | 'o2m' | 'm2m' | 'unknown'): string {
    const labels: Record<string, string> = {
        m2o: 'Many to One',
        o2m: 'One to Many',
        m2m: 'Many to Many',
        unknown: 'Unknown',
    };

    return labels[type] || 'Unknown';
}

/**
 * Check if relation is a system relation
 */
export function isSystemRelation(relation: Relation): boolean {
    return relation.collection.startsWith('cms_') || relation.related_collection?.startsWith('cms_') || false;
}

/**
 * Filter relations by type
 */
export function filterRelationsByType(relations: Relation[], type: 'm2o' | 'o2m' | 'm2m' | 'all') {
    if (type === 'all') return relations;

    return relations.filter((r) => getRelationType(r) === type);
}
